import React, {useContext, useEffect, useState, useRef} from 'react';
import {Button as AntDButton} from "antd";
import {
    AddCircleRounded,
    ArrowForwardRounded,
    GroupsRounded,
    SchoolRounded,
    UploadFileRounded
} from "@mui/icons-material";
import useCurrentShortcut from "@hooks/useCurrentShortcut";
import Button from "@common/Button";
import {NavigationContext} from "@common/context/NavigationContext";
import {UserContext} from "@context/UserContext";
import {TemplateContext} from "@common/context/TemplateContext";
import CircularLoader from "@common/CircularLoader";
import TruncatedText from "../utils/TruncatedText";
import StartBulkClassUpdateDialog from "../schoolClasses/BulkClassUpdate/StartBulkClassUpdateDialog";

const TeacherClassShortcutDashboardWidget = ({ classes }) => {
    const [loading, setLoading] = useState(true);
    const [teacherClasses, setTeacherClasses] = useState([]);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const [isBulkClassUpdateDialogOpen, setIsBulkClassUpdateDialogOpen] = useState(false);
    const { loadShortcutByUrl } = useCurrentShortcut();
    const { isDrawerVisible, setDrawerComponentName, setDrawerVisible, setDrawerLocation } = useContext(NavigationContext);
    const { user } = useContext(UserContext);
    const { shortcut } = useContext(TemplateContext);
    const cardsWrapperRef = useRef(null);
    const cardRefs = useRef([]);
    const liveRegionRef = useRef(null);
    const isFirstRender = useRef(true);

    const loadClasses = async () => {
        setLoading(true)
        try {
            const response = await fetch('/web/ui/myClasses/viewMyClasses');
            const data = await response.json();

            setLoading(false);
            setTeacherClasses(data);
        } catch (error) {
            console.error('Failed to load teacher classes:', error);
        }
    };

    useEffect(() => {
        if(!isDrawerVisible) loadClasses()
    }, [isDrawerVisible]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        loadClasses();
    }, [shortcut]);


    // Announce to screen readers when focused item changes
    useEffect(() => {
        if (teacherClasses.length > 0 && liveRegionRef.current) {
            const currentClass = teacherClasses[focusedIndex];
            liveRegionRef.current.textContent = `${currentClass.className}, ${focusedIndex + 1} of ${teacherClasses.length}`;
        }
    }, [focusedIndex, teacherClasses]);

    const handleAddToClassButton = () => {
        loadShortcutByUrl('/web/ui/view/classes/schoolClasses', {allClasses: true})
    }

    const handleCreateClassButton = () => {
        setDrawerComponentName('AddClassDrawer');
        setDrawerLocation('right');
        setDrawerVisible(true);
    }

    const handleBulkAddButton = () => {
        if(user?.brandCodes.length > 1){
            setIsBulkClassUpdateDialogOpen(true);
        } else {
            loadShortcutByUrl('/web/ui/view/classes/schoolClasses/bulkClassUpdate');
        }
    }

    // Handle keyboard navigation in carousel
    const handleCarouselKeyDown = (e) => {
        if (teacherClasses.length === 0) return;

        let newIndex = focusedIndex;

        switch(e.key) {
            case 'ArrowLeft':
            case 'ArrowUp':
                e.preventDefault();
                newIndex = focusedIndex > 0 ? focusedIndex - 1 : teacherClasses.length - 1;
                break;
            case 'ArrowRight':
            case 'ArrowDown':
                e.preventDefault();
                newIndex = focusedIndex < teacherClasses.length - 1 ? focusedIndex + 1 : 0;
                break;
            case 'Home':
                e.preventDefault();
                newIndex = 0;
                break;
            case 'End':
                e.preventDefault();
                newIndex = teacherClasses.length - 1;
                break;
            default:
                return;
        }

        setFocusedIndex(newIndex);

        // Scroll the focused card into view
        if (cardRefs.current[newIndex]) {
            cardRefs.current[newIndex].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center'
            });
        }
    };

    if(loading){
        return(
            <CircularLoader
                displayInElement={true}
            />
        )
    }

    return(
        <div className={'ch-class-shortcut-widget'}>
            <h2 id="my-classes-heading">My classes</h2>
            <div
                ref={liveRegionRef}
                className="ch-sr-only"
                role="status"
                aria-live="polite"
                aria-atomic="true"
            />
            <div className={'ch-class-shortcut-widget-wrapper'}>
                <div
                    className={`ch-class-shortcut-widget-cards-wrapper ${teacherClasses.length === 0 ? 'empty' : ''}`}
                    role={teacherClasses.length > 0 ? "region" : undefined}
                    aria-label={teacherClasses.length > 0 ? "My classes carousel" : undefined}
                    aria-roledescription={teacherClasses.length > 0 ? "carousel" : undefined}
                    onKeyDown={handleCarouselKeyDown}
                >
                    <div
                        className={'ch-class-shortcut-widget-cards'}
                        ref={cardsWrapperRef}
                    >
                        {teacherClasses.length > 0 && teacherClasses.map((teacherClass, index) => {
                            return(
                                <AntDButton
                                    key={teacherClass.classId}
                                    className={'ch-card-button ch-card-button-filled'}
                                    onClick={() => loadShortcutByUrl(`/web/ui/view/classes/schoolClasses/class/${teacherClass.classId}`)}
                                    ref={el => cardRefs.current[index] = el}
                                    aria-label={`${teacherClass.className}, ${teacherClass.schoolYearDescription ? `Year ${teacherClass.schoolYearDescription}, ` : ''}${teacherClass.totalStudents} students`}
                                    aria-roledescription="slide"
                                    aria-setsize={teacherClasses.length}
                                    aria-posinset={index + 1}
                                    tabIndex={index === focusedIndex ? 0 : -1}
                                    onFocus={() => setFocusedIndex(index)}
                                >
                                    <div className={'ch-class-shortcut-widget-header'}>
                                        <p><TruncatedText text={teacherClass.className} truncateAt={12} maxLength={14} /></p>
                                        <GroupsRounded fontSize={'large'} aria-hidden="true" />
                                    </div>
                                    <div className={'ch-class-shortcut-widget-content'}>
                                        <p>{teacherClass.schoolYearDescription && `${teacherClass.schoolYearDescription}`}</p>
                                        <p>{teacherClass.totalStudents} student{teacherClass.totalStudents === 1 ? `` : `s`}</p>
                                    </div>
                                    <div>
                                        <ArrowForwardRounded fontSize={'large'} aria-hidden="true" />
                                    </div>
                                </AntDButton>
                            )
                        })}
                        {teacherClasses.length === 0 &&
                            <div className={"ch-class-shortcut-widget-cards-wrapper empty"}>
                                <p className={'ch-warning-text'} style={{width: 'fit-content'}}>You're not a teacher of any classes with students assigned.</p>
                                <div className={'ch-class-shortcut-empty-buttons'}>
                                    <Button type={'outline'} icon={<AddCircleRounded />} onClick={() => handleAddToClassButton()}>Add yourself to a class</Button>
                                    <Button
                                        type={'outline'}
                                        icon={<SchoolRounded/>}
                                        onClick={() => handleCreateClassButton()}
                                        data-gtm-value="dashboard-create-class-no-class"
                                    >
                                        Create class
                                    </Button>
                                    <Button
                                        type={'outline'}
                                        icon={<UploadFileRounded/>}
                                        onClick={() => handleBulkAddButton()}
                                        data-gtm-value="dashboard-bulk-update-no-class"
                                    >
                                        Bulk class update
                                    </Button>
                                </div>
                            </div>
                        }
                    </div>
                </div>
                {teacherClasses.length > 0 &&
                    <div className={'ch-class-shortcut-widget-actions'}>
                        <Button
                            type={'outline'}
                            onClick={() => handleCreateClassButton()}
                            data-gtm-value="dashboard-create-class"
                        >
                            Create class <SchoolRounded/>
                        </Button>
                        <Button
                            type={'outline'}
                            onClick={() => handleBulkAddButton()}
                            data-gtm-value="dashboard-bulk-update"
                        >
                            Bulk class update <UploadFileRounded/>
                        </Button>
                    </div>
                }
            </div>
            <StartBulkClassUpdateDialog
                open={isBulkClassUpdateDialogOpen}
                setClose={() => setIsBulkClassUpdateDialogOpen(false)}
                callback={() => {
                    loadShortcutByUrl('/web/ui/view/classes/schoolClasses/bulkClassUpdate')
                }}
            />
        </div>
    )
};

export default TeacherClassShortcutDashboardWidget;